
import { mapGetters } from "vuex";
import { UserRole } from "~/enums";
import { User } from "~/models/User";

export default {
  data() {
    return {
      clipped: false,
      drawer: true,
      fixed: false,
      accountLoading: false,
      enableIcons: false,

      phone: "1-800-593-5036",
      email: "help@expertretainer.com",
      helpDialog: false,

      accountMenuItems: [
        {
          icon: "mdi-account-circle",
          title: "My Account",
          to: "/my-account",
          // to: "/users/{current_user}"
        },
        {
          icon: "mdi-exit-to-app",
          title: "Logout",
          click: function () {
            this.accountLoading = true;
            this.$auth.logout().then(() => {
              this.accountLoading = false;

              document.location.href = "/login";
            });
          }.bind(this),
        },
      ],
      allPrimaryMenuItems: [
        // Client nav is the mockups' `Dashboard | Billing | My account`
        // (founder decision 1) — the mockup's own word was Billing; asana-1207
        // renamed the nav label (and the page) to Payments. Case Submissions
        // is demoted into the My Account
        // dropdown below — its route is unchanged and every link to it still
        // works, but note that /submissions is the only screen listing
        // archived, expired and cancelled cases, so that list is now two clicks
        // deep. Raised with the founder; the mockup nav was chosen anyway.
        {
          icon: "mdi-view-dashboard-outline",
          title: "Dashboard",
          to: "/",
          // Required: "/" is a prefix of every route, so without exact matching
          // nuxt-link marks this item active on every page and the scrubs
          // underline never moves.
          exact: true,
          visibility: function (user) {
            return user.user_role === UserRole.Client;
          },
        },
        {
          icon: "mdi-credit-card-outline",
          title: "Payments",
          to: "/my-account/billing",
          visibility: function (user) {
            return user.user_role === UserRole.Client;
          },
        },
        {
          // Admins still reach /submissions from the primary nav; only clients
          // get the mockup's three-item bar. Preserving this exact visibility
          // expression is what keeps admins whole.
          icon: "mdi-post-outline",
          title: "Case Submissions",
          to: "/submissions",
          visibility: function (user) {
            return user.is_admin;
          },
        },
        {
          icon: "mdi-briefcase-outline",
          title: "My Cases",
          to: "/my-cases",
          visibility: ["admin"],
        },
        {
          icon: "mdi-post-outline",
          title: "Case Matches",
          to: "/expert-matches",
          visibility: function (user) {
            return user.user_role === UserRole.Expert;
          },
        },
        {
          icon: "mdi-account-tie",
          title: "Experts",
          to: "/experts",
          visibility: ["admin"],
        },
        {
          icon: "mdi-account",
          title: "Users",
          to: "/users",
          visibility: ["admin"],
        },
        {
          title: "Expert Profile",
          to: "/expert-profile",
          visibility: function (user) {
            return user.user_role === UserRole.Expert && user.experts.length;
          },
        },
        {
          title: "Matching Settings",
          to: "/expert-matching",
          visibility: function (user) {
            return user.user_role === UserRole.Expert && user.experts.length;
          },
        },
        {
          icon: "mdi-tools",
          title: "System",
          id: "system",
          visibility: ["admin"],
          subItems: [
            {
              icon: "mdi-shape",
              title: "Professions",
              to: "/professions",
            },
            {
              icon: "mdi-shape",
              title: "Specialties",
              to: "/specialties",
            },
            {
              icon: "mdi-shape",
              title: "Subspecialties",
              to: "/sub-specialties",
            },
            {
              icon: "mdi-shape",
              title: "Mailer Templates",
              to: "/mailer-templates",
            },
          ],
        },
        {
          icon: "mdi-tools",
          title: "More",
          id: "system",
          visibility: ["admin"],
          subItems: [
            {
              icon: "mdi-message-badge-outline",
              title: "Submission Events",
              to: "/submission-events",
            },
            {
              icon: "mdi-message-badge-outline",
              title: "Notifications",
              to: "/notifications",
            },
            {
              icon: "mdi-currency-usd",
              title: "Credits",
              to: "/credits",
            },

            {
              icon: "mdi-attachment",
              title: "Attachments",
              to: "/attachments",
            },
            {
              title: "Expert Matches",
              to: "/expert-matches",
            },
          ],
        },
        {
          title: "My Account",
          icon: "mdi-account",
          id: "my-account",
          to: "/my-account",
          subItems: [
            {
              title: "Account Settings",
              id: "account-settings",
              to: "/my-account/profile",
            },
            {
              icon: "mdi-drama-masks",
              title: "Stop Impersonation",
              click: function () {
                this.accountLoading = true;
                User.endSwitchUser().then(() => {
                  this.accountLoading = false;
                  window.location.href = this.$nuxt.$config.baseURL;
                });
              }.bind(this),
              visibility: () => {
                return this.$auth.user.impersonating;
              },
            },
            {
              icon: "mdi-exit-to-app",
              title: "Logout",
              id: "logout",
              click: function () {
                this.accountLoading = true;
                this.$auth.logout().then(() => {
                  this.accountLoading = false;
                  this.$router.push({
                    path: "/login",
                  });
                });
              }.bind(this),
            },
          ],
        },
      ],
      miniVariant: false,
      resendVerificationOpen: false,
      verificationResent: false,
      showMobile: false,
    };
  },
  mounted() {
    if (typeof this.$route.query.debug !== "undefined") {
      this.$config.debug = this.$route.query.debug == "1" ? true : false;
    }
  },
  computed: {
    ...mapGetters("app", ["title"]),

    liveChatActive() {
      return typeof window !== "undefined" && !!window.HubSpotConversations;
    },
    activePrimaryMenuItems() {
      return this.allPrimaryMenuItems
        .filter((item) => this.showItemForCurUser(item, this.$auth.user))
        .map((item) => {
          if (item.subItems) {
            item.subItems = item.subItems.filter((subItem) =>
              this.showItemForCurUser(subItem, this.$auth.user)
            );
          }
          return item;
        });
    },
    userName() {
      if (!this.$auth.loggedIn) return "";
      return this.$auth.user.first_name;
    },
    showSystemMenu() {
      return this.$auth.user.is_admin;
    },
    mobileMenuItems() {
      return this.activePrimaryMenuItems
        .map((item) => {
          if (item.subItems) {
            return [{ br: true }, ...item.subItems];
          }
          return item;
        })
        .flat();
    },
    menuStyleBreakpoint() {
      if (this.activePrimaryMenuItems.length < 4) {
        return "xsOnly";
      } else if (this.activePrimaryMenuItems.length < 7) {
        return "smAndDown";
      }

      return "mdAndDown";
    },
    desktopMenuClass() {
      return {
        "d-none": this.$vuetify.breakpoint[this.menuStyleBreakpoint],
      };
    },
    mobileMenuClass() {
      return {
        "d-none": !this.desktopMenuClass["d-none"],
      };
    },
    userEmailVerified() {
      return this.$auth.user.is_email_verified;
    },
  },
  methods: {
    showChat() {
      window.HubSpotConversations.widget.open();
      this.helpDialog = false;
    },
    decorateTo(to) {
      if (to && to.indexOf("{current_user}") !== -1) {
        to = to.replace("{current_user}", this.$auth.user.id);
      }
      return to;
    },
    showItemForCurUser: function (item, user) {
      if (!item.visibility) return true;

      if (typeof item.visibility == "function") return item.visibility(user);

      const isNonAdmin = item.visibility.indexOf("nonAdminOnly") !== -1;

      if (user.is_admin) return !isNonAdmin;

      if (user.is_super_user)
        return item.visibility.indexOf("super") !== -1 && !isNonAdmin;

      return (
        item.visibility.indexOf("admin") === -1 &&
        item.visibility.indexOf("super") === -1
      );
    },
  },
};
